import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { incidentsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, ShieldAlert, Plus, Filter } from 'lucide-react';

const SEVERITY_CFG = {
  low: { color: '#16A34A', bg: '#DCFCE7' },
  medium: { color: '#CA8A04', bg: '#FEF3C7' },
  high: { color: '#DC2626', bg: '#FEE2E2' },
  critical: { color: '#7F1D1D', bg: '#FEE2E2' },
};

const STATUS_CFG = {
  open: { color: '#DC2626', bg: '#FEE2E2', label: 'Open' },
  acknowledged: { color: '#1A56DB', bg: '#DBEAFE', label: 'Acknowledged' },
  resolved: { color: '#16A34A', bg: '#DCFCE7', label: 'Resolved' },
  closed: { color: '#64748B', bg: '#F1F5F9', label: 'Closed' },
};

export default function IncidentsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  const isAide = user?.role === 'aide';

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      const { data } = await incidentsApi.list(params);
      setIncidents(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, severityFilter]);

  return (
    <div className="space-y-6" data-testid="incidents-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldAlert className="w-6 h-6 text-[#DC2626]" />{isAide ? 'My Incident Reports' : 'Patient Incidents Inbox'}
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{isAide ? 'Reports you have submitted.' : 'Reports submitted by aides — triage and resolve.'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="incident-status-filter"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter || 'all'} onValueChange={(v) => setSeverityFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="incident-severity-filter"><SelectValue placeholder="All severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          {isAide && (
            <Button className="bg-[#DC2626] hover:bg-[#991B1B] text-white" onClick={() => navigate('/incidents/new')} data-testid="new-incident-btn">
              <Plus className="w-4 h-4 mr-1" />Report incident
            </Button>
          )}
        </div>
      </div>

      {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /> : (
        <div className="space-y-2">
          {incidents.map(i => {
            const sev = SEVERITY_CFG[i.severity] || SEVERITY_CFG.medium;
            const st = STATUS_CFG[i.status] || STATUS_CFG.open;
            return (
              <Card key={i.id} className="careiq-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/incidents/${i.id}`)} data-testid={`incident-row-${i.id}`}>
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0" style={{ color: sev.color, backgroundColor: sev.bg }}>
                    {i.severity.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#0A2342]">{i.incident_label}</h3>
                      <Badge style={{ color: st.color, backgroundColor: st.bg }} className="border-0">{st.label}</Badge>
                      {i.patient_name && <span className="text-sm text-[#64748B]">· {i.patient_name}</span>}
                    </div>
                    <p className="text-xs text-[#64748B] mt-1 line-clamp-2">{i.narrative}</p>
                    <div className="text-[10px] text-[#94A3B8] mt-1 flex items-center gap-2 flex-wrap">
                      <span>Reported by {i.reporter_name}</span>
                      <span>·</span>
                      <span>{new Date(i.reported_at).toLocaleString()}</span>
                      {i.location && <><span>·</span><span>📍 {i.location}</span></>}
                      {(i.photos || []).length > 0 && <><span>·</span><span>📷 {i.photos.length} photo{i.photos.length === 1 ? '' : 's'}</span></>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {incidents.length === 0 && <Card className="careiq-card"><CardContent className="p-10 text-center text-[#64748B]">No incidents — that's a good thing.</CardContent></Card>}
        </div>
      )}
    </div>
  );
}
